import ArrowForwardOutlined from '@icons/ArrowForwardOutlined';
import { CampaignSummary, UserNotification } from '@types';
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import BaseCampaignNotification from './BaseCampaignNotification';

type ActivityNotification = { campaign_id: string };

function isValidCampaign(obj: unknown): obj is ActivityNotification {
  return typeof obj === 'object' && obj !== null && 'campaign_id' in obj && typeof (obj as Record<string, unknown>).campaign_id === 'string';
}

const isCampaignSummary = (obj: unknown): obj is CampaignSummary => {
  if (typeof obj !== 'object' || obj === null) return false;
  const o = obj as Record<string, unknown>;
  if (typeof o.campaign_id !== 'string' || typeof o.job_id !== 'string' || !Array.isArray(o.summary)) return false;
  for (const entry of o.summary) {
    if (typeof entry !== 'object' || entry === null) return false;
    const e = entry as Record<string, unknown>;
    if (typeof e.date !== 'string' || typeof e.count !== 'number') return false;
  }
  return true;
};

export interface NavbarMailboxCampaignNotificationProps {
  notification: UserNotification;
  onNavigate: (id: string) => void;
}

const NavbarMailboxCampaignNotification = ({ notification, onNavigate }: NavbarMailboxCampaignNotificationProps) => {
  const navigate = useNavigate();

  const datum = useMemo(() => {
    if (!notification.data) return null;
    if (!isCampaignSummary(notification.data)) return null;
    return notification.data;
  }, [notification.data]);

  const campaign_id = isValidCampaign(notification.data) ? notification.data.campaign_id : null;

  const onNavigated = useCallback(() => {
    navigate(`/campaign/${campaign_id}`);
    onNavigate(notification.id);
  }, [campaign_id, navigate, notification.id, onNavigate]);

  const renderMessage = useCallback(
    (campaignName: string) => {
      return `"${campaignName}" has ${datum?.summary[datum.summary.length - 1].count} new leads.`;
    },
    [datum]
  );

  return (
    <BaseCampaignNotification
      notification={notification}
      onNavigate={onNavigate}
      campaignId={campaign_id}
      notificationType="Campaign activity"
      renderMessage={renderMessage}
      actionIcon={<ArrowForwardOutlined />}
      onAction={onNavigated}
    />
  );
};

export default NavbarMailboxCampaignNotification;
